import type { InputSource } from './InputSystem'

/**
 * Keyboard input source — WASD + arrow keys for movement, R for reset (MOVE-08).
 *
 * Diagonals are normalized to unit length via Math.hypot so "up-right" doesn't
 * move the player √2× faster than cardinal directions (classic 2D top-down bug).
 */
export class KeyboardSource implements InputSource {
  private keys: Phaser.Types.Input.Keyboard.CursorKeys
  private wasd: Record<'W' | 'A' | 'S' | 'D' | 'R', Phaser.Input.Keyboard.Key>
  private lastR = false

  constructor(scene: Phaser.Scene) {
    const kb = scene.input.keyboard
    if (!kb) {
      throw new Error('KeyboardSource requires a scene with keyboard input enabled')
    }
    this.keys = kb.createCursorKeys()
    // addKeys('W,A,S,D,R') returns a map keyed by each letter.
    this.wasd = kb.addKeys('W,A,S,D,R') as Record<
      'W' | 'A' | 'S' | 'D' | 'R',
      Phaser.Input.Keyboard.Key
    >
  }

  read(): { x: number; y: number } {
    const xRaw =
      (this.keys.right.isDown || this.wasd.D.isDown ? 1 : 0) -
      (this.keys.left.isDown || this.wasd.A.isDown ? 1 : 0)
    const yRaw =
      (this.keys.down.isDown || this.wasd.S.isDown ? 1 : 0) -
      (this.keys.up.isDown || this.wasd.W.isDown ? 1 : 0)
    const len = Math.hypot(xRaw, yRaw)
    if (len === 0) return { x: 0, y: 0 }
    return { x: xRaw / len, y: yRaw / len }
  }

  /** Rising-edge detection: true exactly once per R press, not while held. */
  readReset(): boolean {
    const rDown = this.wasd.R.isDown
    const edge = rDown && !this.lastR
    this.lastR = rDown
    return edge
  }
}
